/**
 * Roles & Permissions Tab
 *
 * Left panel: role list with create/delete
 * Right panel: permission grid for selected role
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  listRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  listPermissions,
  setRolePermissions,
  setRoleTenants,
  listTenants,
} from '../../services/adminApi';
import type { Role, Permission, RoleWithPermissions } from '../../types/roles';
import type { Tenant } from '../../services/adminApi';

// Domain display order
const DOMAIN_ORDER = [
  'admin', 'users', 'roles', 'tasks', 'files', 'email',
  'calendar', 'knowledge', 'cogito', 'contacts', 'search',
  'steering', 'billing', 'audit', 'gdpr', 'support', 'tenants', 'voice',
];

export default function RolesTab() {
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const isSuperadmin = user?.is_superadmin ?? false;

  const [roles, setRoles] = useState<Role[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleWithPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Permission grid state: set of enabled permission IDs
  const [enabledPermIds, setEnabledPermIds] = useState<Set<number>>(new Set());
  // Tenant assignment state for non-global roles
  const [enabledTenantIds, setEnabledTenantIds] = useState<Set<string>>(new Set());
  // Track if changes were made
  const [dirty, setDirty] = useState(false);

  // Create role form
  const [showCreate, setShowCreate] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDisplayName, setNewRoleDisplayName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');

  // Edit metadata
  const [editingMeta, setEditingMeta] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        listRoles(),
        listPermissions(),
      ]);
      setRoles(rolesRes.roles);
      setAllPermissions(permsRes.permissions);

      if (isSuperadmin) {
        try {
          const tenantsRes = await listTenants();
          setTenants(tenantsRes.tenants);
        } catch {
          // Non-superadmin won't have access
        }
      }
    } catch {
      setError(t('roles.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t, isSuperadmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectRole = async (role: Role) => {
    try {
      const detail = await getRole(role.id);
      setSelectedRole(detail);
      const permIds = new Set((detail.permissions || []).map((p: Permission) => p.id));
      setEnabledPermIds(permIds);
      setEnabledTenantIds(new Set(detail.active_tenants || []));
      setDirty(false);
      setEditingMeta(false);
    } catch {
      setError('Failed to load role details');
    }
  };

  const handleTogglePermission = (permId: number) => {
    setEnabledPermIds(prev => {
      const next = new Set(prev);
      if (next.has(permId)) {
        next.delete(permId);
      } else {
        next.add(permId);
      }
      return next;
    });
    setDirty(true);
  };

  const handleToggleDomain = (domain: string, enable: boolean) => {
    const domainPerms = allPermissions.filter(p => p.domain === domain);
    setEnabledPermIds(prev => {
      const next = new Set(prev);
      for (const p of domainPerms) {
        if (enable) {
          next.add(p.id);
        } else {
          next.delete(p.id);
        }
      }
      return next;
    });
    setDirty(true);
  };

  const handleToggleTenant = (tenantId: string) => {
    setEnabledTenantIds(prev => {
      const next = new Set(prev);
      if (next.has(tenantId)) {
        next.delete(tenantId);
      } else {
        next.add(tenantId);
      }
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await setRolePermissions(selectedRole.id, Array.from(enabledPermIds));

      if (isSuperadmin && !selectedRole.is_global) {
        await setRoleTenants(selectedRole.id, Array.from(enabledTenantIds));
      }

      // Reload
      await loadData();
      const detail = await getRole(selectedRole.id);
      setSelectedRole(detail);
      setEnabledPermIds(new Set((detail.permissions || []).map((p: Permission) => p.id)));
      setEnabledTenantIds(new Set(detail.active_tenants || []));
      setDirty(false);
    } catch {
      setError('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!selectedRole) return;
    setEnabledPermIds(new Set((selectedRole.permissions || []).map((p: Permission) => p.id)));
    setEnabledTenantIds(new Set(selectedRole.active_tenants || []));
    setDirty(false);
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim() || !newRoleDisplayName.trim()) return;
    setSaving(true);
    try {
      await createRole({
        name: newRoleName.trim().toLowerCase().replace(/\s+/g, '-'),
        display_name: newRoleDisplayName.trim(),
        description: newRoleDescription.trim() || undefined,
      });
      setShowCreate(false);
      setNewRoleName('');
      setNewRoleDisplayName('');
      setNewRoleDescription('');
      await loadData();
    } catch {
      setError('Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRole || selectedRole.is_system) return;
    if (!confirm(`Delete role "${selectedRole.display_name}"?`)) return;
    setSaving(true);
    try {
      await deleteRole(selectedRole.id);
      setSelectedRole(null);
      await loadData();
    } catch {
      setError('Failed to delete role');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMeta = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await updateRole(selectedRole.id, {
        display_name: editDisplayName,
        description: editDescription || undefined,
      });
      setEditingMeta(false);
      await loadData();
      const detail = await getRole(selectedRole.id);
      setSelectedRole(detail);
    } catch {
      setError('Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  // Group permissions by domain
  const permissionsByDomain = new Map<string, Permission[]>();
  for (const p of allPermissions) {
    if (!permissionsByDomain.has(p.domain)) {
      permissionsByDomain.set(p.domain, []);
    }
    permissionsByDomain.get(p.domain)!.push(p);
  }

  const sortedDomains = DOMAIN_ORDER.filter(d => permissionsByDomain.has(d));

  if (loading) {
    return <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">{t('roles.title')}</h2>
        <p className="text-sm text-gray-500 mt-1">{t('roles.subtitle')}</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">dismiss</button>
        </div>
      )}

      <div className="flex gap-6">
        {/* Left panel: role list */}
        <div className="w-80 flex-shrink-0">
          {isSuperadmin && (
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="w-full mb-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
            >
              {showCreate ? t('common.cancel') : t('roles.createRole')}
            </button>
          )}

          {showCreate && (
            <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200 space-y-3">
              <input
                type="text"
                placeholder={t('roles.namePlaceholder')}
                value={newRoleName}
                onChange={e => setNewRoleName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <input
                type="text"
                placeholder={t('roles.displayNamePlaceholder')}
                value={newRoleDisplayName}
                onChange={e => setNewRoleDisplayName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <textarea
                placeholder={t('roles.description')}
                value={newRoleDescription}
                onChange={e => setNewRoleDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <button
                onClick={handleCreateRole}
                disabled={saving || !newRoleName.trim() || !newRoleDisplayName.trim()}
                className="w-full px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 text-sm"
              >
                {t('common.create')}
              </button>
            </div>
          )}

          <div className="space-y-2">
            {roles.map(role => (
              <button
                key={role.id}
                onClick={() => handleSelectRole(role)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedRole?.id === role.id
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-gray-900">{role.display_name}</span>
                  {role.is_system && (
                    <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                      {t('roles.systemRole')}
                    </span>
                  )}
                  {role.is_global && (
                    <span className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-600 rounded">
                      {t('roles.globalRole')}
                    </span>
                  )}
                </div>
                {role.description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{role.description}</p>
                )}
                <div className="flex gap-3 mt-2 text-xs text-gray-400">
                  <span>{t('roles.permissionCount', { count: role.permission_count })}</span>
                  <span>{t('roles.userCount', { count: role.user_count })}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right panel: permission grid */}
        <div className="flex-1 min-w-0">
          {!selectedRole ? (
            <div className="text-center py-16 text-gray-400">
              {t('roles.noRoleSelected')}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              {/* Role header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  {editingMeta ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editDisplayName}
                        onChange={e => setEditDisplayName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                      <textarea
                        value={editDescription}
                        onChange={e => setEditDescription(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveMeta}
                          disabled={saving}
                          className="px-3 py-1 bg-amber-600 text-white rounded text-sm hover:bg-amber-700 disabled:opacity-50"
                        >
                          {t('roles.save')}
                        </button>
                        <button
                          onClick={() => setEditingMeta(false)}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                        >
                          {t('roles.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {selectedRole.display_name}
                        </h3>
                        <span className="text-sm text-gray-400">({selectedRole.name})</span>
                        <button
                          onClick={() => {
                            setEditDisplayName(selectedRole.display_name);
                            setEditDescription(selectedRole.description || '');
                            setEditingMeta(true);
                          }}
                          className="text-gray-400 hover:text-gray-600 text-sm"
                        >
                          {t('common.edit')}
                        </button>
                      </div>
                      {selectedRole.description && (
                        <p className="text-sm text-gray-500 mt-1">{selectedRole.description}</p>
                      )}
                    </>
                  )}

                  {selectedRole.is_system && (
                    <p className="text-xs text-amber-600 mt-2">{t('roles.systemRoleWarning')}</p>
                  )}
                </div>

                {isSuperadmin && !selectedRole.is_system && (
                  <button
                    onClick={handleDeleteRole}
                    disabled={saving}
                    className="px-3 py-1.5 bg-red-50 text-red-600 rounded-md text-sm hover:bg-red-100 disabled:opacity-50"
                  >
                    {t('roles.delete')}
                  </button>
                )}
              </div>

              {/* Permission grid */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">{t('roles.editPermissions')}</h4>
                <div className="space-y-4">
                  {sortedDomains.map(domain => {
                    const domainPerms = permissionsByDomain.get(domain) || [];
                    const allEnabled = domainPerms.every(p => enabledPermIds.has(p.id));
                    const someEnabled = domainPerms.some(p => enabledPermIds.has(p.id));
                    const domainLabel = t(`roles.domains.${domain}`, domain);

                    return (
                      <div key={domain} className="border border-gray-100 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-sm font-medium text-gray-800">{domainLabel}</h5>
                          <button
                            onClick={() => handleToggleDomain(domain, !allEnabled)}
                            className="text-xs text-amber-600 hover:text-amber-700"
                          >
                            {allEnabled ? t('roles.deselectAll') : t('roles.selectAll')}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                          {domainPerms.map(perm => (
                            <label
                              key={perm.id}
                              className="flex items-start gap-2 cursor-pointer"
                              title={perm.description || ''}
                            >
                              <input
                                type="checkbox"
                                checked={enabledPermIds.has(perm.id)}
                                onChange={() => handleTogglePermission(perm.id)}
                                className="mt-0.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                ref={el => {
                                  if (el) el.indeterminate = false;
                                }}
                              />
                              <span className="text-sm text-gray-700">{perm.display_name}</span>
                            </label>
                          ))}
                        </div>
                        {/* Indeterminate indicator for domain header */}
                        {someEnabled && !allEnabled && (
                          <div className="text-xs text-gray-400 mt-1">
                            {domainPerms.filter(p => enabledPermIds.has(p.id)).length}/{domainPerms.length}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tenant assignments (superadmin, non-global roles) */}
              {isSuperadmin && !selectedRole.is_global && tenants.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">{t('roles.activeTenants')}</h4>
                  <p className="text-xs text-gray-400 mb-3">{t('roles.activeTenantsDescription')}</p>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                    {tenants.map(tenant => (
                      <label key={tenant.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enabledTenantIds.has(tenant.id)}
                          onChange={() => handleToggleTenant(tenant.id)}
                          className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                        <span className="text-sm text-gray-700">{tenant.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Save/Cancel */}
              {dirty && (
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm font-medium"
                  >
                    {saving ? t('userDetail.saving') : t('roles.save')}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 text-sm"
                  >
                    {t('roles.cancel')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
